/**
 * FavoritesSection Component
 *
 * Displays a section of favorited menu items in the sidebar navigation.
 * Features:
 * - Section header with star icon and favorites count badge
 * - Empty state message when no favorites
 * - List of favorited items with icons and labels
 * - "Ver mais" expansion for items beyond maxVisibleItems
 * - Hover effects and navigation on click
 * - Responsive styling matching sidebar theme
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FavoriteMenuItem } from '@/types/favorites';
import * as LucideIcons from 'lucide-react';

/**
 * Props interface for FavoritesSection component
 */
export interface FavoritesSectionProps {
  /** Array of favorited menu items to display */
  favorites: FavoriteMenuItem[];
  /** Callback when a favorite item is clicked for navigation */
  onItemClick: (item: FavoriteMenuItem) => void;
  /** Maximum number of items to show before "Ver mais" button (default: 8) */
  maxVisibleItems?: number;
  /** Whether the sidebar is expanded (affects visibility) */
  isExpanded: boolean;
  /** Additional CSS classes for the section wrapper */
  className?: string;
}

/**
 * FavoritesSection Component
 *
 * Renders a dedicated section for favorite menu items in the sidebar.
 */
export function FavoritesSection({
  favorites,
  onItemClick,
  maxVisibleItems = 8,
  isExpanded,
  className,
}: FavoritesSectionProps) {
  // State for "Ver mais" expansion toggle
  const [showAll, setShowAll] = useState(false);

  // State for tracking hovered item for visual feedback
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  /**
   * Compute visible favorites based on showAll state
   * Memoized to prevent unnecessary recalculations
   */
  const visibleFavorites = useMemo(() => {
    if (showAll || favorites.length <= maxVisibleItems) {
      return favorites;
    }
    return favorites.slice(0, maxVisibleItems);
  }, [favorites, showAll, maxVisibleItems]);

  /**
   * Calculate remaining items count for "Ver mais" button
   */
  const remainingCount = favorites.length - maxVisibleItems;
  const hasMoreItems = remainingCount > 0;

  /**
   * Handle favorite item click
   * Calls parent callback and can be extended for analytics
   */
  const handleItemClick = (item: FavoriteMenuItem) => {
    onItemClick(item);
    // Future: Track analytics event "favorite_item_clicked"
  };

  /**
   * Get Lucide icon component by name
   * Falls back to Star icon if icon name is invalid
   */
  const getIconComponent = (iconName: string): React.ComponentType<any> => {
    try {
      // Type assertion to access dynamic icon names
      const IconComponent = (LucideIcons as any)[iconName];

      if (IconComponent && typeof IconComponent === 'function') {
        return IconComponent;
      }

      // Fallback to Star icon
      return Star;
    } catch (error) {
      console.warn(`[FavoritesSection] Invalid icon name: ${iconName}`);
      return Star;
    }
  };

  // Don't render section wrapper if no favorites (empty state will render inline)
  if (favorites.length === 0) {
    return null;
  }

  return (
    <div className={cn('border-b border-sidebar-border pb-4 mb-4', className)}>
      {/* Section Header */}
      <div className="flex items-center gap-2 px-3 py-2 mb-2">
        <Star className="h-4 w-4 text-yellow-500 flex-shrink-0" />
        <span className="text-sm font-semibold text-sidebar-foreground">Favoritos</span>
        <Badge variant="secondary" className="ml-auto">
          {favorites.length}
        </Badge>
      </div>

      {/* Favorites List */}
      <nav role="navigation" aria-label="Menu de favoritos" className="space-y-1">
        {visibleFavorites.map((item) => {
          const isHovered = hoveredItemId === item.id;
          const IconComponent = getIconComponent(item.icon);

          return (
            <Link
              key={item.id}
              to={item.path}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => setHoveredItemId(item.id)}
              onMouseLeave={() => setHoveredItemId(null)}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200',
                'hover:bg-sidebar-hover',
                'group'
              )}
            >
              {/* Item Icon */}
              <IconComponent
                className={cn(
                  'h-[18px] w-[18px] flex-shrink-0 transition-colors duration-200',
                  isHovered ? 'text-yellow-500' : 'text-muted-foreground'
                )}
              />

              {/* Item Label */}
              <span
                className={cn(
                  'text-sm transition-all duration-200 truncate',
                  isHovered ? 'font-medium text-sidebar-foreground' : 'font-normal text-sidebar-foreground/80'
                )}
              >
                {item.label}
              </span>

              {/* Optional Badge (from metadata) */}
              {item.metadata?.badge && (
                <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                  {item.metadata.badge}
                </Badge>
              )}
            </Link>
          );
        })}
      </nav>

      {/* "Ver mais" / "Ver menos" Button */}
      {hasMoreItems && (
        <div className="mt-2 px-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            className="w-full justify-start text-xs text-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-hover h-8"
          >
            <ChevronDown
              className={cn(
                'h-3 w-3 mr-2 transition-transform duration-200',
                showAll && 'rotate-180'
              )}
            />
            <span>
              {showAll ? 'Ver menos' : `+${remainingCount} mais...`}
            </span>
          </Button>
        </div>
      )}
    </div>
  );
}
